// src/data/claimTally.js - THE CLAIM TALLY (task list section 493). Bob, 2026-09-28:
//   "for each end user to have a read only counter tallying for the individual member - the total number
//    of claims submitted and amount, awarded and amount, unsuccessful and amount; for local chairs it should
//    display the same information for everyone in his local committee of adjustment displaying those names
//    in accordance with office as matrix which the names are links to their internal files"
//   "the general chairs should be treated the same as the lcs except at their level of office, within their
//    specific roles"
//
// READ-ONLY and DERIVED. Nothing here is stored and nothing is written: the tally is counted from the claims
// the viewer can already read (claimQueries.js - the rules decide what that is), grouped by member with the
// same identity rule as the member file (memberFile.js memberKeyOfClaim), and by office (the Local
// Chairperson seat, the claim's scopeKey).
//
// WHAT EACH NUMBER IS, so no reader has to guess (ruling 161 and ruling 68.2 govern):
//   Submitted     every claim past draft. Amount = the amount claimed ON the claim (amountClaimed). A claim
//                 with no amount is counted and reported as "no amount on N", never as $0.
//   Awarded       outcome allowed in full, allowed in part or settled. Amount = RECOVERED money with a
//                 document or pay record behind it (ruling 161). A figure with no document is shown apart
//                 as "unsourced" and never added in.
//   Unsuccessful  outcome denied (merits or procedure), withdrawn or abandoned. Amount = what those claims
//                 claimed.
//   Open          past draft with no outcome recorded - NOT counted as won or lost (68.2).
//   At a Board    progressed to a Board - neither won nor lost.
//   Submitted = Awarded + Unsuccessful + Open + At a Board, always; the tally checks itself.
// Drafts are the author's alone (ruling 56): never in anyone's tally, not even his own "submitted".
import { outcomeMeta, isSourcedAmount } from './claimOutcome';
import { memberKeyOfClaim } from './memberFile';

const num = (v) => {
  if (typeof v === 'number') return isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') { const n = parseFloat(v.replace(/[$,\s]/g, '')); return isFinite(n) ? n : null; }
  return null;
};

export function claimBucket(c) {
  if (!c || c.status === 'draft') return 'draft';
  const m = outcomeMeta(c.outcome);
  if (!m) return 'open';
  if (m.key === 'progressed_to_board') return 'board';
  if (m.win === true) return 'awarded';
  if (m.adverse) return 'unsuccessful';
  return 'open';
}

export function emptyTally() {
  return {
    submitted: { n: 0, amount: 0, noAmount: 0 },
    awarded: { n: 0, amount: 0, unsourced: 0, unsourcedOn: 0, unknownOn: 0 },
    unsuccessful: { n: 0, amount: 0, noAmount: 0 },
    open: { n: 0 }, board: { n: 0 },
  };
}

export function addToTally(t, c) {
  const b = claimBucket(c);
  if (b === 'draft') return t;
  const claimed = num(c.amountClaimed);
  t.submitted.n++;
  if (claimed === null) t.submitted.noAmount++; else t.submitted.amount += claimed;
  if (b === 'awarded') {
    t.awarded.n++;
    const rec = num(c.amountRecovered);
    if (rec === null) t.awarded.unknownOn++;
    else if (isSourcedAmount({ ...c, amountRecovered: rec })) t.awarded.amount += rec;
    else { t.awarded.unsourced += rec; t.awarded.unsourcedOn++; }
  } else if (b === 'unsuccessful') {
    t.unsuccessful.n++;
    if (claimed === null) t.unsuccessful.noAmount++; else t.unsuccessful.amount += claimed;
  } else if (b === 'board') t.board.n++;
  else t.open.n++;
  return t;
}

export function tallyClaims(claims) {
  return (Array.isArray(claims) ? claims : []).reduce(addToTally, emptyTally());
}

export function reconciles(t) {
  return t.submitted.n === t.awarded.n + t.unsuccessful.n + t.open.n + t.board.n;
}

// The viewer's OWN claims: the ones his account is the claimant on (memberFile's account join).
export function ownClaims(claims, uid) {
  return (Array.isArray(claims) ? claims : []).filter((c) => { const k = memberKeyOfClaim(c); return !!(k && uid && k.uid === uid); });
}

// THE MATRIX. Offices (seats) -> members -> tally. `seatLabel(scopeKey)` names an office; a claim with no
// scopeKey lands in "(no office on the claim)" so the totals always reconcile to the claims that went in.
export const NO_OFFICE = '(no office on the claim)';
export function tallyMatrix(claims, seatLabel) {
  const label = typeof seatLabel === 'function' ? seatLabel : (k) => k;
  const offices = new Map();
  for (const c of (Array.isArray(claims) ? claims : [])) {
    if (claimBucket(c) === 'draft') continue;
    const k = memberKeyOfClaim(c); if (!k) continue;
    const office = String(c.scopeKey || '') || NO_OFFICE;
    if (!offices.has(office)) offices.set(office, { office, label: office === NO_OFFICE ? NO_OFFICE : (label(office) || office), members: new Map(), total: emptyTally() });
    const o = offices.get(office);
    if (!o.members.has(k.key)) o.members.set(k.key, { key: k.key, name: k.name || k.email || '(unnamed)', joinedBy: k.by, tally: emptyTally() });
    addToTally(o.members.get(k.key).tally, c);
    addToTally(o.total, c);
  }
  const out = Array.from(offices.values()).map((o) => ({
    office: o.office, label: o.label, total: o.total,
    members: Array.from(o.members.values()).sort((a, b) => a.name.localeCompare(b.name)),
  }));
  out.sort((a, b) => (a.office === NO_OFFICE) - (b.office === NO_OFFICE) || a.office.localeCompare(b.office));
  const grand = tallyClaims((Array.isArray(claims) ? claims : []).filter((c) => memberKeyOfClaim(c)));
  return { offices: out, grand };
}

export const money = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
